import React, { useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useOnHoverOutside } from 'src/hooks/useOnHoverOutside';
import { NavLinkWithPopoverProps } from '../NavLink.types';
import {
  getNavlinkWithPopoverStyles,
  NavLinkWithPopoverContainer,
  LinksMenu,
  MenuWrapper,
} from './NavLinkWithPopover.styles';
import { ChevronDownIcon, BadgeNewIcon } from 'src/packages/icons';
import { ExternalLink, Text, Flex } from 'src/packages/core';

const NavLinkWithPopover: React.FC<NavLinkWithPopoverProps> = ({
  links,
  title,
  mainLink = undefined,
  popoverCustomContent = undefined,
  theme = 'dark',
}: NavLinkWithPopoverProps) => {
  const contextTheme = useTheme();

  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [isMenuDropDownOpen, setMenuDropDownOpen] = useState(false);

  const closeHoverMenu = () => {
    setMenuDropDownOpen(false);
  };

  useOnHoverOutside(dropdownRef, closeHoverMenu);

  const {
    menuItemSublabelColor,
    menuItemBgColorHover,
    menuItemLabelColorHover,
    popoverMenuBgColor,
    menuItemLabelColor,
    iconColor,
  } = useMemo(() => {
    return getNavlinkWithPopoverStyles(theme);
  }, [theme]);

  const renderPopoverContent = useMemo(() => {
    if (popoverCustomContent) {
      return (
        <MenuWrapper>
          <LinksMenu
            popoverMenuBgColor={popoverMenuBgColor}
            menuItemBgColorHover={menuItemBgColorHover}
            menuItemLabelColorHover={menuItemLabelColorHover}
          >
            {popoverCustomContent}
          </LinksMenu>
        </MenuWrapper>
      );
    } else {
      return (
        <MenuWrapper>
          <LinksMenu
            popoverMenuBgColor={popoverMenuBgColor}
            menuItemBgColorHover={menuItemBgColorHover}
            menuItemLabelColorHover={menuItemLabelColorHover}
          >
            <ul className={'navlink-menu'}>
              {links &&
                links.map((item, index) => {
                  const { icon: Icon, label, sublabel, url, target, restricted } = item;

                  if (restricted && restricted()) return null;

                  return (
                    <ExternalLink key={index} link={url || ''} target={target}>
                      <Flex width={'100%'} alignItems={'center'} gap={4}>
                        {Icon && <Icon size={24} color={iconColor} />}

                        <Flex flexDirection={'column'} width={'100%'} justifyContent={'center'}>
                          <Flex position={'relative'} width={'max-content'}>
                            <Text
                              variant={'p2'}
                              className={'link-item-label'}
                              color={menuItemLabelColor}
                              text={t(label)}
                            />

                            {item.new && (
                              <BadgeNewIcon
                                classNames={theme === 'dark' ? 'orange' : 'red'}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  ...(contextTheme.rtl && { left: -25 }),
                                  ...(!contextTheme.rtl && { right: -25 }),
                                }}
                              />
                            )}
                          </Flex>

                          {sublabel && (
                            <Text variant={'p3'} text={t(sublabel)} color={menuItemSublabelColor} />
                          )}
                        </Flex>
                      </Flex>
                    </ExternalLink>
                  );
                })}
            </ul>
          </LinksMenu>
        </MenuWrapper>
      );
    }
  }, [popoverCustomContent]);

  return (
    <NavLinkWithPopoverContainer ref={dropdownRef}>
      <Flex onMouseOver={() => setMenuDropDownOpen(true)} style={{ cursor: 'pointer' }}>
        <ExternalLink link={mainLink && mainLink}>
          <Flex className={'main-link-label'} alignItems={'center'}>
            <Text
              variant='p2'
              style={{ whiteSpace: 'nowrap' }}
              color={contextTheme.colors.palette.white}
              text={title}
              mr={2}
            />
            <ChevronDownIcon color={contextTheme.colors.palette.white} />
          </Flex>
        </ExternalLink>
      </Flex>

      {isMenuDropDownOpen && renderPopoverContent}
    </NavLinkWithPopoverContainer>
  );
};

export default NavLinkWithPopover;
